
import { map, retry } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { Logger } from '@app/core/logger.service';

const jsonContentType = 'application/json';
const logKey = 'TelvisAir Api';
const log = new Logger(`Service ${logKey}`);

export interface IBaseAPIService {
  GET(url: string, params?: any): Observable<any>;
  POST(url: string, body: any, params?: any): Observable<any>;
  PATCH(url: string, body: any, params?: any): Observable<any>;
  PUT(url: string, body: any, params?: any): Observable<any>;
  DELETE(url: string, params?: any): Observable<any>;
}

@Injectable()
export class BaseAPIService implements IBaseAPIService {


  constructor(private readonly _http: HttpClient, private readonly authenticationService: AuthenticationService) { }

  /**
  * GET function makes a rest api call with get method and returns the Observable.
  * @param url :string - Request url string
  */
  public GET(url: string, params?: any) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': jsonContentType,
        Authorization: `Bearer ${this.token}`
      })
    };

    return this._http.get(url, httpOptions)
      .pipe(retry(3));
  }

  /**
  * GET function makes a rest api call with get method with headers as params and returns the Observable.
  * @httpOptions HttpHeaders object
  */
  public GetWithHeaders(url: string, headersObj: any) {

    headersObj['content-type'] = jsonContentType;
    headersObj.Authorization = `Bearer ${this.token}`;

    const headers = new HttpHeaders(headersObj);

    const httpOptions = {
      headers: headers
    };

    return this._http.get(url, httpOptions)
      .pipe(retry(3));
  }

  /**
  * GET function makes a rest api call with get method with headers as params and returns text response.
  * @httpOptions HttpHeaders object
  */
  public GetTextWithHeaders(url: string, headersObj: any) {

    headersObj['content-type'] = jsonContentType;
    headersObj.Authorization = `Bearer ${this.token}`;

    const headers = new HttpHeaders(headersObj);

    const httpOptions = {
      headers: headers,
      responseType: 'text' as 'json'
    };

    return this._http.get(url, httpOptions)
      .pipe(retry(3));
  }

  public PostFormDataWithHeaders(url: string, headersObj: any, body?: any) {
    if (body !== undefined)
    {
      body = body || {};
    }
    headersObj['Content-Disposition'] = 'multipart/form-data';
    headersObj['Authorization'] = `Bearer ${this.token}`;

    const headers = new HttpHeaders(headersObj);

    const httpOptions = {
      headers: headers
    };

    return this._http.post(url, body, httpOptions)
      .pipe(retry(1));
  }

  public PostWithHeaders(url: string, headersObj: any, body?: any) {
    if (body !== undefined)
    {
      body = body || {};
    }

    headersObj["Access-Control-Allow-Origin"] = '*';
    headersObj['content-type'] = jsonContentType;
    headersObj['Authorization'] = `Bearer ${this.token}`;
    headersObj['Accept'] = jsonContentType;
  const headers = new HttpHeaders(headersObj);

    const httpOptions = {
      headers: headers
    };

    return this._http.post(url, body, httpOptions)
      .pipe(retry(1));
  }

  public PutWithHeaders(url: string, headersObj: any, body?: any) {
    if (body !== undefined) {
      body = body || {};
    }
    headersObj['content-type'] = jsonContentType;
    headersObj['Authorization'] = `Bearer ${this.token}`;
    headersObj['Accept'] = jsonContentType;

    const headers = new HttpHeaders(headersObj);

    const httpOptions = {
      headers: headers
    };

    return this._http.put(url, body, httpOptions)
      .pipe(retry(1));
  }

  public PatchWithHeaders(url: string, headersObj: any, body?: any) {
    if (body !== undefined) {
      body = body || {};
    }

    headersObj['content-type'] = jsonContentType;
    headersObj['Authorization'] = `Bearer ${this.token}`;
    headersObj['Accept'] = jsonContentType;

    const headers = new HttpHeaders(headersObj);

    const httpOptions = {
      headers: headers
    };

    return this._http.patch(url, body, httpOptions);
  }

  public DeleteWithHeaders(url: string, headersObj: any) {

    headersObj['content-type'] = jsonContentType;
    headersObj['Authorization'] = `Bearer ${this.token}`;
    headersObj['Accept'] = jsonContentType;

    const headers = new HttpHeaders(headersObj);

    const httpOptions = {
      headers: headers
    };

    return this._http.delete(url, httpOptions).pipe(retry(1));
  }

  public DownloadAsText(url: string, param?: any) {
    const headers: HttpHeaders = new HttpHeaders({ 'Authorization': `Bearer ${this.token}` });
    return this._http.get(url, { headers: headers, responseType: 'text' });
  }

  /**
   * POST function makeas a rest API call with post method and returns the Observable
   * @param url :string - Request url String
   * @param data :any - Data Object
   */

  public POST(url: string, body: any, params?: any): Observable<any> {
    const headers: HttpHeaders = new HttpHeaders({
      'Authorization': `Bearer ${this.token}`,
      'Content-Type': jsonContentType,
      'Accept': jsonContentType
    });

    return this._http.post(url, body, { headers: headers });
  }


  public DOWNLOAD(url: string, body: any, params?: any): Observable<Blob> {
    const headers: HttpHeaders = new HttpHeaders({
      'Authorization': `Bearer ${this.token}`,
      'Accept': jsonContentType
    });
    return this._http.post(url, body, { headers: headers, responseType: "blob" }).pipe(map(this.parseData));
  }

  public DOWNLOADGet(url: string, params?: any): Observable<Blob> {
    const headers: HttpHeaders = new HttpHeaders({
      'Authorization': `Bearer ${this.token}`,
      'Accept': jsonContentType
    });
    return this._http.get(url, { headers: headers, responseType: "blob" }).pipe(map(this.parseData));
  }

  private readonly parseData = (res: any) => {
    return res || {};
  };

  /**
   * PATCH function makeas a rest API call with post method and returns the Observable
   * @param url :string - Request url String
   * @param data :any - Data Object
   */

  public PATCH(url: string, body: any, params?: any): Observable<any> {
    const headers: HttpHeaders = new HttpHeaders({ 'Authorization': `Bearer ${this.token}` });
    return this._http.patch(url, body, { headers: headers, responseType: "json" });
  }

  /**
   * PUT function makeas a rest API call with put method and returns the Observable
   * @param url :string - Request url String
   * @param data :any - Data Object
   */

  public PUT(url: string, body: any, params?: any): Observable<any> {
    const headers: HttpHeaders = new HttpHeaders({ 'Authorization': `Bearer ${this.token}` });
    return this._http.put(url, body, { headers: headers, responseType: "json" });

  }

  /**
   * DELETE function makeas a rest API call with delete method and returns the Observable
   * @param url :string - Request url String
   */
  public DELETE(url: string, params?: any): Observable<any> {
    const headers: HttpHeaders = new HttpHeaders({ 'Authorization': `Bearer ${this.token}` });
    return this._http.delete(url, { headers: headers, responseType: "json" });
  }

  /**
   * handleError function handles the API exceptions
   * @param error:any
   */
  get token(): string {
    const token = sessionStorage.getItem("token") ? sessionStorage.getItem("token").replace('"', '') : '';
    if (token === "") {
      log.error("token not available... login again");
    }
    return token;
  }

}
